import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { images } from '../../images'
import { GoogleService, LiteralsService } from '../../services'
import { checkSessionAction, setIsSigningInAction, setNotificationAction } from '../../store/actions'
import { IStoreState } from '../../store/states'
import StyledDiv from './styles'

// Google renders its own button and only takes a pixel width, capped at 400 by the library.
const GOOGLE_BUTTON_MAX_WIDTH = 400

// Google only exists on the builds that carry a web client id, the same rule the app follows.
export const isGoogleSignInAvailable = GoogleService.isConfigured

interface IProps extends DispatchProp<AnyAction> {
  language: string
}

/**
 * "Continue with Google", wherever a screen offers it. Our pill is the one on screen; Google's
 * own button sits invisible on top of it, because only Google's button hands back a credential.
 * Renders nothing on builds without a Google client id.
 */
class GoogleSignInButton extends PureComponent<IProps> {
  private gsiContainer = React.createRef<HTMLDivElement>()

  public componentDidMount(): void {
    if (!isGoogleSignInAvailable) return

    GoogleService
      .setup(this.onCredential)
      .then(() => {
        const container = this.gsiContainer.current
        if (!container) return

        const width = Math.min(container.clientWidth || GOOGLE_BUTTON_MAX_WIDTH, GOOGLE_BUTTON_MAX_WIDTH)
        GoogleService.renderButton(container, width)
      })
      .catch(error => {
        console.error('Could not set up Google sign in', error)
      })
  }

  public render(): ReactNode {
    if (!isGoogleSignInAvailable) return null

    return (
      <StyledDiv>
        <button type="button" className="google-pill" onClick={ this.handlePress }>
          <img src={ images['googleIcon'] } alt="" />
          { LiteralsService.get('continueWithGoogle') }
        </button>
        <div className="google-gsi" ref={ this.gsiContainer } />
      </StyledDiv>
    )
  }

  // Reached when the click missed Google's invisible button: it is at most 400px wide, so on a
  // wider pill the sides fall through to us, and it is not there at all when Google's script
  // could not load. Hand the click to Google's button when it exists (the user's click is what
  // lets it open its popup); otherwise Google's prompt is the way in.
  private handlePress = async () => {
    const { dispatch } = this.props

    const googleButton = this.gsiContainer.current?.querySelector<HTMLElement>('[role="button"]')
    if (googleButton) {
      googleButton.click()
      return
    }

    try {
      await GoogleService.prompt()
    } catch (error) {
      console.error('Google sign in is unavailable', error)
      dispatch(setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      }))
    }
  }

  // Google hands back an id_token; from there it is the app's standard session check, which the
  // gateway resolves against Google's JWKS and turns into the BoD user.
  private onCredential = () => {
    const { dispatch } = this.props

    dispatch(setIsSigningInAction())
    dispatch(checkSessionAction())
  }
}

const mapStateToProps = ({ configuration }: IStoreState) => ({
  // Redraws the pill's literal when the language selector changes the language: Google's own
  // button carries no visible text, so this label is the one on screen.
  language: configuration.language
})

export default connectRedux(mapStateToProps, GoogleSignInButton) as React.ComponentType<{}>
